import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getUserProfile } from '../lib/api/userApi';
import { updateSessionTimestamp } from '../lib/api/chatApi';
import signalRService from '../lib/api/signalRService';
import appStateManager from '../lib/utils/appStateManager';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

// Set to true to enable debug logs, false to disable
const DEBUG = false; // Debug logs disabled for production
// Inactivity check interval in milliseconds (5 minutes)
const INACTIVITY_CHECK_INTERVAL = 5 * 60 * 1000;

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [isInitializing, setIsInitializing] = useState(true);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState('');
  const [prefetchComplete, setPrefetchComplete] = useState(false);
  const [prefetchError, setPrefetchError] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [isRapportMode, setIsRapportMode] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [lastActivityTime, setLastActivityTime] = useState(new Date());
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const isLoadingRef = useRef(false);
  // Add a set to track processed message IDs to prevent duplicates
  const processedMessageIds = useRef(new Set());
  // Keep track of the previous user to detect user changes
  const previousUserRef = useRef(null);
  
  // Reset the chat context completely (for user logout/change)
  const resetContext = () => {
    if (DEBUG) console.log('Resetting chat context completely');
    setMessages([]);
    setSessionId(null);
    setUserName('');
    setPrefetchComplete(false);
    setPrefetchError(null);
    setSystemPrompt('');
    setIsRapportMode(false);
    setCurrentQuestionId(null);
    setLastActivityTime(new Date());
    setSubscriptionRequired(false);
    processedMessageIds.current.clear();
    isLoadingRef.current = false;
  };
  
  // Initialize the chat session
  const initializeChat = async () => {
    if (DEBUG) console.log('Initializing chat session');
    
    try {
      // Make sure SignalR connection is active
      await signalRService.ensureConnection();
      
      // Reset messages for a fresh start
      setMessages([]);
      processedMessageIds.current.clear();
      setLastActivityTime(new Date());
      updateSessionTimestamp();
      
      return true;
    } catch (error) {
      // Check if this is a subscription error specifically tagged by signalRService
      if (error.isSubscriptionError) {
        if (DEBUG) console.log('SignalR connection failed due to subscription requirement');
        setSubscriptionRequired(true);
        setPrefetchError('Subscription required to connect to chat. Please subscribe to continue.');
        return false;
      }
      
      // For other WebSocket errors, check if it might be a subscription issue
      if (error.message && (
          error.message.includes('WebSocket failed to connect') || 
          error.message.includes('Error establishing SignalR connection'))) {
        
        if (DEBUG) console.log('SignalR connection failed, checking subscription status...');
        
        // Check if this is due to subscription issue using our state manager
        try {
          const billingStatus = await appStateManager.getSubscriptionStatus();
          
          if (!billingStatus.currentValidSubscription) {
            // This is a subscription error
            setSubscriptionRequired(true);
            setPrefetchError('Subscription required to connect to chat. Please subscribe to continue.');
            if (DEBUG) console.log('Connection failed due to inactive subscription');
          } else {
            // This is some other connection error
            setPrefetchError(error.message || 'Failed to connect to chat server.');
            if (DEBUG) console.log('Connection failed for reasons other than subscription');
          }
        } catch (billingError) {
          // If we can't check billing status, assume it's a general error
          setPrefetchError(error.message || 'Failed to connect to chat server.');
          console.error('Error checking billing status:', billingError);
        }
      } else {
        // For other errors, just show the error message
        setPrefetchError(error.message || 'Failed to set up chat.');
      }
      
      return false;
    }
  };

  // Send a message using SignalR
  const sendMessage = async (text) => {
    if (!sessionId) {
      console.error('No active session ID');
      return { success: false, error: 'No active session ID' };
    }
    
    try {
      // Send message through signalRService
      await signalRService.sendMessage(sessionId, text);
          return { success: true };
    } catch (error) {
      console.error('Error sending message:', error);
      return { success: false, error: error.message || 'Unknown error sending message' };
    }
  };
  
  // Check if user has changed and reset if needed
  useEffect(() => {
    if (previousUserRef.current && user && previousUserRef.current.id !== user.id) {
      if (DEBUG) console.log('User changed, resetting chat context');
      resetContext();
    }
    previousUserRef.current = user;
  }, [user]);
  
  // Effect to subscribe to SignalR events when user is available
  useEffect(() => {
    // Skip if there's no user
      if (!user) {
        return;
      }
      
    // Set loading flag to prevent race conditions
    isLoadingRef.current = true;
    
    const setupChat = async () => {
      try {
        if (DEBUG) console.log('Setting up chat session');
        
        // Get user profile data for chat
        try {
          const profile = await getUserProfile();
          if (profile && profile.username) {
            setUserName(profile.username);
            if (DEBUG) console.log('User profile loaded:', profile.username);
          }
        } catch (profileError) {
          console.error('Error loading user profile:', profileError);
          // Continue anyway - non-critical
        }
        
        // Message handler
        const messageHandler = (message) => {
          if (DEBUG) console.log("Received message:", message);
          
          // Skip if message is redacted or empty
          if (message.isRedacted || !message.message) return;
          
          // Skip if we've already processed this message ID to prevent duplicates
          const messageId = message.messageId || `${Date.now()}-${Math.random()}`;
          if (processedMessageIds.current.has(messageId)) {
            if (DEBUG) console.log(`Skipping duplicate message with ID: ${messageId}`);
            return;
          }
          
          // Add the ID to our processed set
          processedMessageIds.current.add(messageId);
          
          // Format the message for display
          const formattedMessage = {
            id: messageId,
            text: message.message,
            sender: message.isFromUser ? 'user' : 'bot',
            suggestedResponses: message.suggestedResponses || [],
            sessionId: message.sessionId || sessionId,
            timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
            isQuickTimeEvent: message.isQuickTimeEvent || false, // Preserve QTE flag
            isFirstMessage: message.isFirstMessage || false // Preserve isFirstMessage flag
          };
          
          // Add to messages
          setMessages(prev => [...prev, formattedMessage]);
          updateSessionTimestamp();
        };
        
        // Session messages handler
        const sessionMessagesHandler = (response) => {
          if (DEBUG) console.log("Received current session messages:", response);
          if (response && response.messages && response.messages.length > 0) {
            // Filter out redacted messages
            const filteredMessages = response.messages.filter(msg => !msg.isRedacted);
            
            // Clear previously processed message IDs since we're getting a fresh session
            processedMessageIds.current.clear();
            
            // Track new message IDs
            filteredMessages.forEach(msg => {
              const messageId = msg.messageId || `historic-${Date.now()}-${Math.random()}`;
              processedMessageIds.current.add(messageId);
            });
            
            // Format messages for display
            const formattedMessages = filteredMessages.map(msg => ({
              id: msg.messageId || `historic-${Date.now()}-${Math.random()}`,
              text: msg.message,
              sender: msg.isFromUser ? 'user' : 'bot',
              suggestedResponses: msg.suggestedResponses || [],
              sessionId: msg.sessionId || sessionId,
              timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
              isHistoric: false, // These are current session messages
              isQuickTimeEvent: msg.isQuickTimeEvent || false, // Preserve QTE flag
              isFirstMessage: msg.isFirstMessage || false // Preserve isFirstMessage flag
            }));
            
            // Set current session messages
            setMessages(formattedMessages);
            updateSessionTimestamp();
          }
        };
        
        // Session ID handler
        const sessionIdHandler = (receivedSessionId) => {
          if (DEBUG) console.log("Received session ID from SignalR:", receivedSessionId);
          
          if (receivedSessionId && typeof receivedSessionId === 'string') {
            setSessionId(receivedSessionId);
            updateSessionTimestamp();
          }
        };
        
        // Register handlers
        const unsubscribeMessage = signalRService.onMessage(messageHandler);
        const unsubscribeSessionMessages = signalRService.onSessionMessages(sessionMessagesHandler);
        const unsubscribeSessionId = signalRService.onSessionId(sessionIdHandler);
        
        try {
          // Initialize the SignalR connection
          await signalRService.ensureConnection();
          
          setPrefetchComplete(true);
          if (DEBUG) console.log('Chat setup complete');
        } catch (connectionError) {
          // Check if this is a subscription error
          if (connectionError.isSubscriptionError) {
            if (DEBUG) console.log('Connection failed due to subscription requirement');
            setSubscriptionRequired(true);
            setPrefetchError('Subscription required to connect to chat. Please subscribe to continue.');
          } else {
            // Handle other connection errors
            console.error('Error establishing SignalR connection:', connectionError);
            setPrefetchError(connectionError.message || 'Failed to connect to chat server.');
          }
          
          // We'll still return the unsubscribe functions even if connection failed
        }
        
        // Unsubscribe from events when component unmounts
        return () => {
          unsubscribeMessage();
          unsubscribeSessionMessages();
          unsubscribeSessionId();
          
          // No need to disconnect - let the service manage the connection
        };
      } catch (error) {
        console.error('Error setting up chat:', error);
        setPrefetchError(error.message || 'Failed to set up chat');
      } finally {
        setIsInitializing(false);
        isLoadingRef.current = false;
      }
    };
    
    const cleanup = setupChat();
    return () => {
      // Call cleanup function for event handlers
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [user, sessionId]);
  
  // Update last activity time when messages change
  useEffect(() => {
    if (messages.length > 0) {
      setLastActivityTime(new Date());
      updateSessionTimestamp();
      if (DEBUG) console.log('Updated last activity time due to new message');
    }
  }, [messages]);
  
  // Periodically check for inactivity and reset session if needed
  useEffect(() => {
    if (!user) return;
    
    const checkInactivity = async () => {
      // Don't check if we're already loading data
      if (isLoadingRef.current) return;
      
      const now = new Date();
      const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);
      
      if (lastActivityTime < fifteenMinutesAgo) {
        if (DEBUG) console.log('Chat inactive for 15+ minutes, resetting chat...');
        
        // Set loading flag to prevent race conditions
        isLoadingRef.current = true;
        
        try {
          // Clear messages immediately to prevent showing old messages
          setMessages([]);
          processedMessageIds.current.clear();
          
          // Initialize a fresh chat session
          await initializeChat();
          
          // Update activity time
          setLastActivityTime(new Date());
        } catch (error) {
          console.error('Error resetting chat after inactivity:', error);
        } finally {
          // Clear loading flag
          isLoadingRef.current = false;
        }
      }
    };
    
    const intervalId = setInterval(checkInactivity, INACTIVITY_CHECK_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [user, lastActivityTime]);
  
  // Method to reset chat state (useful for starting a new conversation)
  const resetChat = async () => {
    // Don't reset if already loading
    if (isLoadingRef.current) return false;
    
    // Set loading flag
    isLoadingRef.current = true;
    
    try {
      // Reset chat session
      setMessages([]);
      processedMessageIds.current.clear();
      const success = await initializeChat();
      
      if (DEBUG) console.log('Reset chat session');
      return success;
    } catch (error) {
      console.error('Error resetting chat:', error);
      return false;
    } finally {
      // Clear loading flag
      isLoadingRef.current = false;
    }
  };
  
  // Get the connection state from the service
  const getConnectionState = () => {
    return signalRService.getConnectionState();
  };
  
  // Provide access to all chat data and functions
  const value = {
    isInitializing,
    messages,
    setMessages,
    sessionId,
    userName,
    systemPrompt,
    setSystemPrompt,
    isRapportMode,
    setIsRapportMode,
    currentQuestionId,
    setCurrentQuestionId,
    resetChat,
    resetContext,
    lastActivityTime,
    setLastActivityTime,
    prefetchComplete,
    prefetchError,
    sendMessage,
    initializeChat,
    getConnectionState,
    // Computed connection state properties for backward compatibility
    get connectionEstablished() {
      return signalRService.isConnected;
    },
    get signalRConnection() {
      return signalRService.connection;
    },
    subscriptionRequired,
    setSubscriptionRequired
  };
  
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext; 